import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { WorkflowStatus } from "~/api/types.generated";
import {
  StyledTable,
  TableContainer,
} from "~/components/Admin/Group/GroupsTable";
import { useUserInvitationSearch } from "~/components/Admin/User/useUserInvitationSearch";
import ChecklistFilterDropdown from "~/components/Checklist/ChecklistFilterDropdown/ChecklistFilterDropdown";
import { PrimaryButton, SecondaryButton } from "~/components/common/buttons";
import { AddIcon } from "~/components/common/icons";
import {
  MenuItem,
  StyledSelect,
  StyledTextField,
} from "~/components/common/inputs";
import { ThematicBreak } from "~/components/common/layout";
import Dialog from "~/components/generic/Dialog";
import LoadingSpinner from "~/components/generic/LoadingSpinner";
import {
  PortfolioFragment,
  useCreatePortfolioMutation,
  useDeletePortfolioMutation,
  useOrgUsersQuery,
  usePortfoliosQuery,
  useUpdatePortfolioMutation,
} from "~/components/Portfolio/PortfolioAPI.generated";
import { UpdatePortfolioDialog } from "~/components/Portfolio/UpdateDialog";
import {
  WorkflowStatus as WorkflowStatusEnum,
  getWorkflowStatusDisplay,
} from "~/components/Projects/constants";
import SearchBar from "~/components/SearchBar";
import { fgColor } from "~/styles/mixins";

import { PortfolioMenu } from "./PortfolioMenu";

interface PortfoliosPageProps {
  orgSlug: string;
}

const ClickableTd = styled.td`
  cursor: pointer;
  &:hover {
    ${fgColor.blue()};
    color: $link-color;
    text-decoration: none;
  }
`;

export const PortfoliosPage = ({ orgSlug }: PortfoliosPageProps) => {
  const search = useUserInvitationSearch();
  const [selectedStatus, setSelectedStatus] = useState<WorkflowStatus | null>(
    null
  );
  const { data, loading } = usePortfoliosQuery({
    variables: {
      orgSlug,
      search: search.debouncedSearchTerm ? search.debouncedSearchTerm : "",
      workflowStatus: selectedStatus
        ? WorkflowStatusEnum[selectedStatus as keyof typeof WorkflowStatusEnum]
        : null,
    },
  });
  const { data: usersData, loading: usersLoading } = useOrgUsersQuery({
    variables: { orgSlug },
  });
  const [updateRequest] = useUpdatePortfolioMutation();
  const [createPortfolio] = useCreatePortfolioMutation();
  const [deletePortfolio] = useDeletePortfolioMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [portfolioName, setPortfolioName] = useState("");
  const [dealManagerId, setDealManagerId] = useState("");
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<PortfolioFragment | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreatePortfolio = async () => {
    setCreateError(null);
    try {
      await createPortfolio({
        variables: {
          orgSlug,
          name: portfolioName,
          dealManagerId: dealManagerId || null,
        },
        refetchQueries: ["Portfolios"],
      });
      setIsCreateModalOpen(false);
      setPortfolioName("");
      setDealManagerId("");
    } catch (error: any) {
      const message = error?.message || "An error occurred.";
      if (
        message.includes("duplicate key") ||
        message.includes("already exists")
      ) {
        setCreateError("A deal room with this name already exists.");
      } else {
        setCreateError(message);
      }
    }
  };

  const handleRowClick = (slug: string) => {
    const absolutePath = `${window.location.origin}/portfolios/${slug}`;
    window.location.href = absolutePath;
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      search.setSearchTerm(e.target.value);
    },
    [search]
  );

  const handleEdit = (portfolio: PortfolioFragment) => {
    setSelectedPortfolio(portfolio);
    setIsUpdateModalOpen(true);
  };

  const handleDelete = async (portfolioId: string) => {
    try {
      await deletePortfolio({
        variables: {
          orgSlug,
          portfolioId,
        },
        refetchQueries: ["Portfolios"],
      });
    } catch (error) {
      console.error("Error deleting portfolio:", error);
    }
  };

  const handleUpdatePortfolio = async (
    status: WorkflowStatus,
    name: string,
    enableProjects: boolean,
    enableQAndA: boolean,
    headerEnabled: boolean,
    dealManager: string,
    systemSize: number,
    removeSystemSize: boolean
  ) => {
    try {
      if (selectedPortfolio) {
        await updateRequest({
          variables: {
            orgSlug,
            portfolioId: selectedPortfolio.id,
            status,
            name,
            enableProjects,
            enableQAndA,
            headerEnabled,
            dealManagerId: dealManager,
            systemSize,
            removeSystemSize,
          },
          refetchQueries: ["Portfolios"],
        });
        setIsUpdateModalOpen(false);
        setSelectedPortfolio(null);
      }
    } catch (error) {
      console.error("Error updating portfolio:", error);
    }
  };

  const portfolios = data?.portfolios || [];
  const displayStatus = getWorkflowStatusDisplay(orgSlug);
  const allStatuses = Object.entries(displayStatus).map(([key, value]) => ({
    key,
    value,
    alwaysShow: false,
  }));

  return (
    <div>
      <h2>Deal Rooms</h2>
      <ThematicBreak />
      <div className="d-flex align-items-start justify-content-between mb-2 mt-3">
        <div className="col-5 p-0">
          <SearchBar
            keyword={search.searchTerm}
            onChange={handleSearchChange}
            renderInputGroupPrepend={() => (
              <div className="prepend">
                {search.searchPending ? (
                  <LoadingSpinner />
                ) : (
                  <i className="icon icon--search-dark" />
                )}
              </div>
            )}
            clearable
            onClear={() => search.clearSearch()}
          />
        </div>
        <div className="col-3 p-0">
          <ChecklistFilterDropdown
            getButtonText={(_, selectedItems) => {
              const selectedKey =
                selectedItems[0] as keyof typeof displayStatus;
              return selectedKey === null
                ? "All"
                : displayStatus[selectedKey] || "All";
            }}
            items={allStatuses}
            title={"Filter by Status"}
            selectedOptions={[selectedStatus]}
            onSelected={([selected]) => setSelectedStatus(selected)}
          >
            {({ item, selected }) => [
              <span key="ok" className="filter-dropdown__selected">
                {selected && <i className="icon icon--ok-blue" />}
              </span>,
              item.value,
            ]}
          </ChecklistFilterDropdown>
        </div>
        <div
          className="col d-flex justify-content-end"
          style={{ paddingRight: 0 }}
        >
          <PrimaryButton
            onClick={() => {
              setCreateError(null);
              setPortfolioName("");
              setDealManagerId("");
              setIsCreateModalOpen(true);
            }}
          >
            <AddIcon />
            <span>New Deal Room</span>
          </PrimaryButton>
        </div>
      </div>
      <TableContainer>
        <StyledTable className="table mb-3">
          <thead>
            <tr>
              <th style={{ textAlign: "left", width: "30%" }}>Name</th>
              <th style={{ textAlign: "left", width: "30%" }}>Manager</th>
              <th style={{ textAlign: "left", width: "20%" }}>Status</th>
              <th style={{ textAlign: "left", width: "15%" }}>Created At</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {portfolios.map((portfolio) => (
              <tr key={portfolio.id}>
                <ClickableTd onClick={() => handleRowClick(portfolio.slug)}>
                  {portfolio.name}
                </ClickableTd>
                <td>{portfolio.dealManager?.fullName}</td>
                <td>
                  {portfolio.status ? displayStatus[portfolio.status] : ""}
                </td>
                <td>{new Date(portfolio.createdDate).toLocaleDateString()}</td>
                <td>
                  <PortfolioMenu
                    portfolio={portfolio}
                    onEdit={() => handleEdit(portfolio)}
                    onDelete={() => handleDelete(portfolio.id)}
                    loading={loading}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>
      <Dialog
        isOpen={isCreateModalOpen}
        onRequestClose={() => {
          setIsCreateModalOpen(false);
          setCreateError(null);
        }}
        header="Create New Deal Room"
      >
        <div>
          {createError && (
            <div className="alert alert-danger">{createError}</div>
          )}
          <StyledTextField
            label="Name"
            value={portfolioName || ""}
            onChange={(e) => setPortfolioName(e.target.value)}
            fullWidth
          />
          <div style={{ marginTop: 16 }}>
            <StyledSelect
              label="Deal Manager"
              value={dealManagerId || ""}
              onChange={(event) => setDealManagerId(event.target.value)}
              fullWidth
            >
              {usersLoading ? (
                <MenuItem value="">Loading...</MenuItem>
              ) : (
                usersData?.orgUsers?.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.fullName}
                  </MenuItem>
                ))
              )}
            </StyledSelect>
          </div>
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <SecondaryButton onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleCreatePortfolio}
              style={{ marginLeft: 8 }}
            >
              Create
            </PrimaryButton>
          </div>
        </div>
      </Dialog>
      {selectedPortfolio && (
        <UpdatePortfolioDialog
          isOpen={isUpdateModalOpen}
          portfolioId={selectedPortfolio.id}
          status={selectedPortfolio.status ?? WorkflowStatus.Draft}
          name={selectedPortfolio.name}
          enableProjects={selectedPortfolio.enableProjects}
          enableQAndA={selectedPortfolio.enableQAndA}
          headerEnabled={!!selectedPortfolio.headerEnabled}
          dealManager={selectedPortfolio.dealManager?.id || ""}
          systemSize={selectedPortfolio.systemSize || 0}
          removeSystemSize={false}
          error={null}
          disabled={false}
          orgSlug={orgSlug}
          loading={false}
          open={() => {}}
          reset={() => {
            setSelectedPortfolio(null);
            setIsUpdateModalOpen(false);
          }}
          submit={handleUpdatePortfolio}
        />
      )}
    </div>
  );
};
